import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppConfig } from '../ConfigContext';
import '../index.css';

function Login({ onLogin }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [warning, setWarning] = useState('');
  const [tmaFailed, setTmaFailed] = useState(false);
  const [tmaReason, setTmaReason] = useState('');
  const { refresh } = useAppConfig();

  // Force mode via env: 1 = Telegram only, 0 = Email only
  const REQUIRE_TMA =
    (process.env.REACT_APP_REQUIRE_TMA ??
      process.env.REACT_APP_ENABLE_TMA_DEV /* legacy */) === '1';
  const FORCE_TMA = REQUIRE_TMA;
  const FORCE_EMAIL = !REQUIRE_TMA;

  // Are we inside a Telegram Mini App?
  const isInsideTelegram =
    typeof window !== 'undefined' &&
    !!window.Telegram?.WebApp &&
    (!!window.Telegram.WebApp.initData || !!window.Telegram.WebApp.initDataUnsafe?.user);

  const autoTriedRef = useRef(false);

  // Grab ?ref= for dev/local links or start params consistency
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setReferralCode(ref);
  }, []);

  const attemptTmaLogin = useCallback(async () => {
  const tg = window.Telegram?.WebApp;
  tg?.ready();
  tg?.expand?.(); // optional

  const initData = tg?.initData; // signed querystring
  const startParam = tg?.initDataUnsafe?.start_param || null;

  if (!initData || initData.length === 0) {
    setTmaFailed(true);
    setTmaReason('missing_initdata');
    throw new Error('Telegram auth failed (missing_initdata)');
  }

  const res = await fetch(`${process.env.REACT_APP_API_URL}/api/login/tma`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ initData, referralCode: startParam}),
  });

  if (!res.ok) {
    let bodyText = '';
    let bodyJson = null;
    try { bodyText = await res.text(); } catch {}
    try { bodyJson = JSON.parse(bodyText); } catch {}

    const msg = bodyJson?.error || bodyText || 'Telegram auth failed';
    const reason = bodyJson?.reason || '';
    setTmaFailed(true);
    setTmaReason(reason);
    throw new Error(`${res.status} ${res.statusText} — ${msg}${reason ? ` (${reason})` : ''}`);
  }

  // ✅ Only declare these ONCE
  const data = await res.json(); // { ok, token, new, whitelistEnabled }
  const token = data.token;
  localStorage.setItem('token', token);

  await refresh();

  const meRes = await fetch(`${process.env.REACT_APP_API_URL}/api/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!meRes.ok) throw new Error('Failed to load user data');

  const userData = await meRes.json();

  // ✅ Pass meta so App.js can decide to show the Welcome modal
  onLogin(userData, {
    isNew: !!data.new,
    whitelistEnabled: !!data.whitelistEnabled,
    source: 'tma',
  });
}, [refresh, onLogin]);


  useEffect(() => {
    if (!FORCE_TMA) return;                // Email-only mode → do nothing here
    if (!isInsideTelegram) return;         // Not inside Telegram → show prompt in UI
    if (autoTriedRef.current) return;

    autoTriedRef.current = true;
    setTmaFailed(false);
    setTmaReason('');

    (async () => {
      try {
        await attemptTmaLogin();
      } catch (err) {
        // keep console for debugging; remove if your lint disallows console
        // eslint-disable-next-line no-console
        console.error('TMA auto-login error:', err);
      }
    })();
  }, [FORCE_TMA, isInsideTelegram, attemptTmaLogin]);

  // Email login (email-only mode)
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, referralCode }),
      });

      if (!res.ok) throw new Error('Login failed');

      const data = await res.json();
      const token = data.token;


      setWarning(data.referralWarning || '');
      localStorage.setItem('token', token);

      await refresh();

      const meRes = await fetch(`${process.env.REACT_APP_API_URL}/api/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!meRes.ok) throw new Error('Failed to load user data');

      const userData = await meRes.json();
      onLogin(userData, {
   isNew: !!data.new,                    // will be falsey until you add it server-side
   whitelistEnabled: !!data.whitelistEnabled,
   source: 'email',
 });
    } catch (err) {
      alert('Login error: ' + err.message);
    }
  };

  // UI logic
  const showEmailForm = FORCE_EMAIL;
  const showTmaStatus = FORCE_TMA;

  // Optional: deep link to the bot when we’re not inside Telegram
  const botUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME;
  const botLink = botUsername ? `https://t.me/${botUsername}` : null;

  return (
    <div className="login-wrapper">
      <form onSubmit={showEmailForm ? handleLogin : (e) => e.preventDefault()} className="login-form">
        <h2>Welcome Back 👋</h2>
        <p>Login to continue</p>

        {showEmailForm && (
          <>
            <input
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <input
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              placeholder="Referral code (optional)"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              readOnly={!!new URLSearchParams(window.location.search).get('ref')}
            />
            {warning && <p className="warning">{warning}</p>}
            <button type="submit">Login</button>
          </>
        )}

        {showTmaStatus && (
          <>
            {isInsideTelegram && !tmaFailed && (
              <div style={{ color: '#aaa', fontSize: 12, marginTop: 8 }}>
                Authenticating via Telegram…
              </div>
            )}

            {isInsideTelegram && tmaFailed && (
              <div style={{ marginTop: 12, fontSize: 12 }}>
                Telegram auth failed{tmaReason ? ` (${tmaReason})` : ''}.
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setTmaFailed(false);
                      setTmaReason('');
                      autoTriedRef.current = false;
                      (async () => {
                        try { await attemptTmaLogin(); }
                        catch (err) { /* eslint-disable no-console */ console.error('TMA retry error:', err); /* eslint-enable no-console */ }
                      })();
                    }}
                  >
                    Retry
                  </button>
                  <button
                    type="button"
                    onClick={() => window.Telegram?.WebApp?.close?.()}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {!isInsideTelegram && (
              <div style={{ marginTop: 12, fontSize: 12 }}>
                Please open this app <strong>inside Telegram</strong> to sign in.
                {botLink && (
                  <>
                    <br />
                    <a href={botLink} target="_blank" rel="noreferrer">
                      Open @{botUsername}
                    </a>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </form>
    </div>
  );
}

export default Login;